import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";

import ApiService from "./apiClient";

const DataProviderContext = createContext(null);

export function useDataProvider() {
    const context = useContext(DataProviderContext);

    if (!context) {
        throw new Error(
            "useDataProvider must be used inside <DataProvider>"
        );
    }

    return context;
}

export function DataProvider({ children }) {

    // ============================================================
    // DATA
    // ============================================================

    const mountedRef = useRef(false);

    const [labs, setLabs] = useState([]);
    const [games, setGames] = useState([]);
    const [projects, setProjects] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    // ============================================================
    // LOOKUP
    // ============================================================

    const getLab = (id) => {

        if (id == null) {
            return null;
        }

        return (
            labs.find(
                lab =>
                    String(lab.id) === String(id)
            ) || null
        );
    };


    const getGame = (id) => {

        if (id == null) {
            return null;
        }

        return (
            games.find(
                game =>
                    String(game.id) === String(id)
            ) || null
        );
    };


    const getProject = (id) => {

        if (id == null) {
            return null;
        }

        return (
            projects.find(
                project =>
                    String(project.id) === String(id)
            ) || null
        );
    };


    // ============================================================
    // FETCH LABS
    // ============================================================

    const fetchLabs = async (signal = null) => {

        try {

            const response = await fetch(
                "/api/v1/lab/",
                {
                    credentials: "include",
                    signal: signal || undefined
                }
            );

            const data = await response.json();

            // console.log(
            //     "DATA PROVIDER-fetchLabs:",
            //     data
            // );

            if (!response.ok) {
                throw new Error(
                    data.message ||
                    data.error ||
                    `Unable to load labs: ${response.status}`
                );
            }

            const loadedLabs =
                Array.isArray(data)
                    ? data
                    : Array.isArray(data?.labs)
                        ? data.labs
                        : [];

            // console.log(
            //     "DATA PROVIDER-labs count:",
            //     loadedLabs.length
            // );

            /*
             * Do not update React state if:
             *
             * 1. DataProvider has unmounted
             * 2. Request was aborted
             */
            if (
                !mountedRef.current ||
                signal?.aborted
            ) {
                return loadedLabs;
            }

            setLabs(loadedLabs);

            return loadedLabs;

        } catch (err) {

            if (err.name === "AbortError") {
                return [];
            }

            console.error(
                "DATA PROVIDER-fetchLabs error:",
                err
            );

            if (!mountedRef.current) {
                return [];
            }

            setError(
                err.message ||
                "Unable to load labs."
            );

            setLabs([]);

            return [];
        }
    };


    // ============================================================
    // FETCH GAMES
    // ============================================================

    const fetchGames = async (
        labId = null,
        signal = null
    ) => {

        try {

            let url = "/api/games";

            if (
                labId !== null &&
                labId !== undefined
            ) {
                url +=
                    `?lab_id=${encodeURIComponent(labId)}`;
            }

            const response = await fetch(
                url,
                {
                    credentials: "include",
                    signal: signal || undefined
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message ||
                    data.error ||
                    `Unable to load games: ${response.status}`
                );
            }

            const loadedGames =
                Array.isArray(data)
                    ? data
                    : Array.isArray(data?.games)
                        ? data.games
                        : [];

            // console.log(
            //     "DATA PROVIDER-fetchGames count:",
            //     loadedGames.length
            // );

            /*
             * IMPORTANT
             *
             * The request may finish after DataProvider
             * has already been unmounted.
             */
            if (
                !mountedRef.current ||
                signal?.aborted
            ) {
                return loadedGames;
            }

            /*
             * No lab ID means we loaded ALL games.
             */
            if (
                labId === null ||
                labId === undefined
            ) {

                setGames(loadedGames);

            } else {

                /*
                 * A specific lab was requested.
                 * Merge the returned games into the cache.
                 */
                setGames(prevGames => {

                    /*
                     * This callback should normally execute
                     * immediately, but keep the guard here too.
                     */
                    if (
                        !mountedRef.current ||
                        signal?.aborted
                    ) {
                        return prevGames;
                    }

                    const map = new Map(
                        prevGames.map(game => [
                            String(game.id),
                            game
                        ])
                    );

                    loadedGames.forEach(game => {
                        map.set(
                            String(game.id),
                            game
                        );
                    });

                    return Array.from(map.values());
                });
            }

            return loadedGames;

        } catch (err) {

            if (err.name === "AbortError") {
                return [];
            }

            console.error(
                "DATA PROVIDER-fetchGames error:",
                err
            );

            if (!mountedRef.current) {
                return [];
            }

            setError(
                err.message ||
                "Unable to load games."
            );

            return [];
        }
    };


    // ============================================================
    // FETCH ALL PROJECTS
    // ============================================================

    const fetchProjects = async (signal = null) => {

        try {

            const response = await fetch(
                "/api/student/projects",
                {
                    credentials: "include",
                    signal: signal || undefined
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message ||
                    data.error ||
                    `Unable to load projects: ${response.status}`
                );
            }

            const loadedProjects =
                Array.isArray(data)
                    ? data
                    : Array.isArray(data?.projects)
                        ? data.projects
                        : [];

            // console.log(
            //     "DATA PROVIDER-fetchProjects count:",
            //     loadedProjects.length
            // );

            if (
                !mountedRef.current ||
                signal?.aborted
            ) {
                return loadedProjects;
            }

            setProjects(loadedProjects);

            return loadedProjects;

        } catch (err) {

            if (err.name === "AbortError") {
                return [];
            }

            console.error(
                "DATA PROVIDER-fetchProjects error:",
                err
            );

            if (!mountedRef.current) {
                return [];
            }

            setError(
                err.message ||
                "Unable to load projects."
            );

            setProjects([]);

            return [];
        }
    };


    // ============================================================
    // FETCH SINGLE PROJECT
    // ============================================================

    const fetchProject = async (
        projectId,
        signal = null
    ) => {

        if (
            projectId === null ||
            projectId === undefined
        ) {
            throw new Error(
                "Project ID is required."
            );
        }

        try {

            const response = await fetch(
                `/api/project/${encodeURIComponent(projectId)}`,
                {
                    credentials: "include",
                    signal: signal || undefined
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message ||
                    data.error ||
                    "Failed to fetch project."
                );
            }

            let project = null;

            if (Array.isArray(data)) {

                project = data.find(
                    p =>
                        String(p.id) ===
                        String(projectId)
                );

            } else if (data?.project) {

                project = data.project;

            } else {

                project = data;
            }

            if (!project) {
                throw new Error(
                    "Project not found."
                );
            }

            /*
             * The API request can finish after the
             * DataProvider has disappeared.
             */
            if (
                !mountedRef.current ||
                signal?.aborted
            ) {
                return project;
            }

            /*
             * Update project cache.
             */
            setProjects(prevProjects => {

                if (
                    !mountedRef.current ||
                    signal?.aborted
                ) {
                    return prevProjects;
                }

                const exists = prevProjects.some(
                    p =>
                        String(p.id) ===
                        String(project.id)
                );

                if (exists) {

                    return prevProjects.map(p =>
                        String(p.id) ===
                            String(project.id)
                            ? project
                            : p
                    );
                }

                return [
                    ...prevProjects,
                    project
                ];
            });

            return project;

        } catch (err) {

            if (err.name === "AbortError") {
                return null;
            }

            console.error(
                "DATA PROVIDER-fetchProject error:",
                err
            );

            throw err;
        }
    };


    // ============================================================
    // INITIAL LOAD
    // ============================================================

    useEffect(() => {

        /*
         * Mark this DataProvider instance as mounted
         * BEFORE starting any asynchronous operation.
         */
        mountedRef.current = true;

        const controller =
            new AbortController();

        const loadInitialData = async () => {

            try {

                setLoading(true);
                setError(null);

                // console.log(
                //     "DATA PROVIDER: loading initial data..."
                // );

                await Promise.all([
                    fetchLabs(
                        controller.signal
                    ),

                    fetchGames(
                        null,
                        controller.signal
                    ),

                    fetchProjects(
                        controller.signal
                    )
                ]);

                if (
                    !mountedRef.current ||
                    controller.signal.aborted
                ) {
                    return;
                }

                // console.log(
                //     "DATA PROVIDER: initial data loaded."
                // );

            } catch (err) {

                if (
                    err.name === "AbortError" ||
                    controller.signal.aborted
                ) {
                    return;
                }

                console.error(
                    "DATA PROVIDER LOAD ERROR:",
                    err
                );

                if (mountedRef.current) {
                    setError(
                        err.message ||
                        "Failed to load data."
                    );
                }

            } finally {

                if (
                    mountedRef.current &&
                    !controller.signal.aborted
                ) {
                    setLoading(false);
                }
            }
        };

        loadInitialData();

        /*
         * IMPORTANT CLEANUP
         */
        return () => {

            // console.log(
            //     "DATA PROVIDER: unmounting, cancelling requests."
            // );

            /*
             * Set this FIRST.
             *
             * Any request that completes after this point
             * will NOT be allowed to call setState().
             */
            mountedRef.current = false;

            /*
             * Cancel all initial fetch requests.
             */
            controller.abort();
        };

    }, []);


    // ============================================================
    // CREATE PROJECT
    // ============================================================

    const addData = async (data) => {

        try {

            const response = await fetch(
                "/api/project/create",
                {
                    method: "POST",

                    headers: {
                        "Content-Type":
                            "application/json"
                    },

                    credentials: "include",

                    body: JSON.stringify(data)
                }
            );

            const result =
                await response.json();

            // console.log(
            //     "DATA PROVIDER-addData response:",
            //     result
            // );

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    result.error ||
                    "Failed to create project."
                );
            }

            const project =
                result?.project ||
                result;

            if (!project?.id) {
                throw new Error(
                    "Project was created but no project ID was returned."
                );
            }

            /*
             * The POST may complete after the component
             * using DataProvider has navigated away.
             *
             * Do not update state if provider is gone.
             */
            if (mountedRef.current) {

                setProjects(prevProjects => {

                    if (!mountedRef.current) {
                        return prevProjects;
                    }

                    const exists =
                        prevProjects.some(
                            p =>
                                String(p.id) ===
                                String(project.id)
                        );

                    if (exists) {

                        return prevProjects.map(p =>
                            String(p.id) ===
                                String(project.id)
                                ? project
                                : p
                        );
                    }

                    return [
                        ...prevProjects,
                        project
                    ];
                });
            }

            return project;

        } catch (error) {

            console.error(
                "DATA PROVIDER-addData error:",
                error
            );

            throw error;
        }
    };


    // ============================================================
    // UPDATE PROJECT
    // ============================================================

    const updateData = async (
        id,
        data
    ) => {

        const response =
            await ApiService.update(
                "project",
                id,
                data
            );

        if (mountedRef.current) {

            setProjects(prev =>
                prev.map(project =>
                    String(project.id) ===
                        String(id)
                        ? response
                        : project
                )
            );
        }

        return response;
    };


    // ============================================================
    // DELETE PROJECT
    // ============================================================

    const deleteData = async (id) => {

        await ApiService.delete(
            "project",
            id
        );

        if (mountedRef.current) {

            setProjects(prev =>
                prev.filter(
                    project =>
                        String(project.id) !==
                        String(id)
                )
            );
        }
    };


    // ============================================================
    // OTHER API METHODS
    // ============================================================

    const submitSimulate = async (
        url,
        data
    ) => {

        return await ApiService.simulate(
            url,
            data
        );
    };


    const checkSimulateStatus = async (
        url,
        id
    ) => {

        return await ApiService.simulateStatus(
            url,
            id
        );
    };


    const downloadApk = async (file) => {

        return await ApiService.apkDownload(
            file
        );
    };


    const saveDocument = async (data) => {

        return await ApiService.saveProject(
            data
        );
    };


    // ============================================================
    // CONTEXT VALUE
    // ============================================================

    const dataProviderValue = {

        // Collections
        labs,
        games,
        projects,

        // Lookup
        getLab,
        getGame,
        getProject,

        // Fetch
        fetchLabs,
        fetchGames,
        fetchProjects,
        fetchProject,

        // State
        loading,
        error,

        // Project CRUD
        addData,
        updateData,
        deleteData,

        // Other API
        fetchProtectedData:
            ApiService.getOne,

        submitSimulate,
        checkSimulateStatus,
        downloadApk,
        saveDocument
    };


    // ============================================================
    // PROVIDER
    // ============================================================

    return (
        <DataProviderContext.Provider
            value={dataProviderValue}
        >
            {children}
        </DataProviderContext.Provider>
    );
}

export default DataProvider;